import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  message,
  Spin,
  Tag,
  Space,
  Divider,
  Collapse,
  Badge,
  Tooltip,
  Alert,
  Empty,
} from "antd";
import {
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  ClearOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { ragApi, RAGChatRequest, RAGChatResponse } from "../services/ragApi";
import ChainlitChatArea from "./ChainlitChatArea";
import { getChainlitIframeUrl } from "../services/chainlitUrls";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface RAGMessage {
  id: string;
  type: "user" | "ai";
  content: string;
  timestamp: Date;
  domain?: string;
  sources?: Array<{
    title: string;
    content: string;
    score?: number;
    metadata?: any;
  }>;
  processing_steps?: Array<{
    step: string;
    description: string;
    duration?: number;
  }>;
  metadata?: {
    total_steps: number;
    processing_time: number;
    documents_found: number;
  };
  error?: string;
}

interface RAGChatProps {
  selectedDomain: string;
  domainTitle: string;
  onBack: () => void;
  initialQuery?: string;
}

const RAGChat: React.FC<RAGChatProps> = ({
  selectedDomain,
  domainTitle,
  onBack,
  initialQuery,
}) => {
  const [messages, setMessages] = useState<RAGMessage[]>([
    {
      id: "1",
      type: "ai",
      content: `您好！我是${domainTitle}智能助手，基於RAG技術為您提供專業的文檔檢索和問答服務。您可以詢問任何相關問題，我會從知識庫中為您找到最相關的信息。`,
      timestamp: new Date(),
      domain: selectedDomain,
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showProcessingSteps, setShowProcessingSteps] = useState(true);
  const [useChainlit, setUseChainlit] = useState(false); // 新增：切換開關
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 處理初始查詢
  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      setInputValue(initialQuery);
      // 自動發送初始查詢
      setTimeout(() => {
        handleSendMessage(initialQuery);
      }, 500);
    }
  }, [initialQuery]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue;
    if (!textToSend.trim()) {
      message.warning("請輸入您的問題");
      return;
    }

    const userMessage: RAGMessage = {
      id: Date.now().toString(),
      type: "user",
      content: textToSend,
      timestamp: new Date(),
      domain: selectedDomain,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setLoading(true);

    try {
      const request: RAGChatRequest = {
        message: textToSend,
        domain: selectedDomain,
        session_id: sessionId || undefined,
        options: {
          include_processing_steps: showProcessingSteps,
        },
      };

      const response: RAGChatResponse = await ragApi.sendRAGMessage(request);

      if (response.success && response.data) {
        // Update session ID if new session was created
        if (response.data.session_id && !sessionId) {
          setSessionId(response.data.session_id);
        }

        const aiResponse: RAGMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: response.data.response, // 修改为response字段
          timestamp: new Date(),
          domain: selectedDomain,
          sources: response.data.sources,
          processing_steps: response.data.processing_steps,
          metadata: response.data.metadata,
        };

        setMessages((prev) => [...prev, aiResponse]);
      } else {
        // Handle error response
        const errorMessage: RAGMessage = {
          id: (Date.now() + 1).toString(),
          type: "ai",
          content: "抱歉，處理您的問題時遇到了錯誤。請稍後再試。",
          timestamp: new Date(),
          domain: selectedDomain,
          error: response.error || "未知錯誤",
        };

        setMessages((prev) => [...prev, errorMessage]);
        message.error(response.error || "處理請求時發生錯誤");
      }
    } catch (error: any) {
      console.error("RAG Chat Error:", error);

      const errorMessage: RAGMessage = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: "網絡連接錯誤，請檢查您的網絡連接並重試。",
        timestamp: new Date(),
        domain: selectedDomain,
        error: error.message || "網絡錯誤",
      };

      setMessages((prev) => [...prev, errorMessage]);
      message.error("網絡連接錯誤");
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await ragApi.clearRAGSession(sessionId);
      } catch (error) {
        console.error("Clear session error:", error);
      }
    }

    setMessages([
      {
        id: "1",
        type: "ai",
        content: `您好！我是${domainTitle}智能助手，基於RAG技術為您提供專業的文檔檢索和問答服務。您可以詢問任何相關問題，我會從知識庫中為您找到最相關的信息。`,
        timestamp: new Date(),
        domain: selectedDomain,
      },
    ]);
    setSessionId(null);
    message.success("對話已清空");
  };

  const renderSources = (
    sources?: Array<{
      title: string;
      content: string;
      score?: number;
      metadata?: any;
    }>
  ) => {
    if (!sources || sources.length === 0) return null;

    return (
      <Collapse size="small" style={{ marginTop: 8 }}>
        <Panel
          header={
            <Space>
              <FileTextOutlined />
              <Text strong>參考文檔 ({sources.length})</Text>
            </Space>
          }
          key="sources"
        >
          {sources.map((source, index) => (
            <Card
              key={index}
              size="small"
              style={{ marginBottom: 8 }}
              title={
                <Space>
                  <Badge count={index + 1} size="small" />
                  <Text strong>{source.title}</Text>
                  {source.score && (
                    <Tag color="blue">
                      相關度: {(source.score * 100).toFixed(1)}%
                    </Tag>
                  )}
                </Space>
              }
            >
              <Paragraph
                ellipsis={{ rows: 3, expandable: true, symbol: "展開" }}
                style={{ margin: 0 }}
              >
                {source.content}
              </Paragraph>
            </Card>
          ))}
        </Panel>
      </Collapse>
    );
  };

  const renderProcessingSteps = (
    steps?: Array<{ step: string; description: string; duration?: number }>
  ) => {
    if (!steps || steps.length === 0) return null;

    return (
      <Collapse size="small" style={{ marginTop: 8 }}>
        <Panel
          header={
            <Space>
              <ClockCircleOutlined />
              <Text strong>處理步驟 ({steps.length})</Text>
            </Space>
          }
          key="steps"
        >
          {steps.map((step, index) => (
            <div
              key={index}
              style={{
                marginBottom: 8,
                padding: 8,
                backgroundColor: "#f9f9f9",
                borderRadius: 4,
              }}
            >
              <Space>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <Text strong>{step.step}</Text>
                {step.duration && <Tag color="orange">{step.duration}ms</Tag>}
              </Space>
              <div style={{ marginTop: 4, marginLeft: 20 }}>
                <Text type="secondary">{step.description}</Text>
              </div>
            </div>
          ))}
        </Panel>
      </Collapse>
    );
  };

  const renderMetadata = (metadata?: {
    total_steps: number;
    processing_time: number;
    documents_found: number;
  }) => {
    if (!metadata) return null;

    return (
      <div
        style={{
          marginTop: 8,
          padding: 8,
          backgroundColor: "#f0f8ff",
          borderRadius: 4,
          border: "1px solid #d9d9d9",
        }}
      >
        <Space wrap>
          <Tag icon={<InfoCircleOutlined />} color="blue">
            總步驟: {metadata.total_steps}
          </Tag>
          <Tag icon={<ClockCircleOutlined />} color="orange">
            處理時間: {metadata.processing_time}ms
          </Tag>
          <Tag icon={<FileTextOutlined />} color="green">
            找到文檔: {metadata.documents_found}
          </Tag>
        </Space>
      </div>
    );
  };

  const quickQuestions = [
    "請介紹一下中國瓷器的分類方法",
    "什麼是青花瓷？它有什麼特點？",
    "如何鑑別古代瓷器的真偽？",
    "明清瓷器有什麼區別？",
  ];

  return (
    <Card
      title={
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            type="text"
            size="small"
          >
            返回
          </Button>
          <RobotOutlined />
          <span>{domainTitle} RAG 智能助手</span>
          <Tag color="blue">{selectedDomain}</Tag>
          {sessionId && <Tag color="green">會話: {sessionId.slice(-8)}</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="切換到Chainlit聊天">
            <Button
              onClick={() => setUseChainlit(!useChainlit)}
              type={useChainlit ? "primary" : "default"}
              size="small"
            >
              {useChainlit ? "RAG模式" : "Chainlit模式"}
            </Button>
          </Tooltip>
          <Tooltip
            title={showProcessingSteps ? "隱藏處理步驟" : "顯示處理步驟"}
          >
            <Button
              icon={<InfoCircleOutlined />}
              onClick={() => setShowProcessingSteps(!showProcessingSteps)}
              type={showProcessingSteps ? "primary" : "default"}
              size="small"
              disabled={useChainlit}
            >
              步驟
            </Button>
          </Tooltip>
          <Button
            icon={<ClearOutlined />}
            onClick={clearChat}
            type="text"
            size="small"
            disabled={useChainlit}
          >
            清空對話
          </Button>
        </Space>
      }
      style={{ height: "700px", display: "flex", flexDirection: "column" }}
      bodyStyle={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: "16px",
      }}
    >
      {useChainlit ? (
        /* 使用真正的Chainlit聊天界面 */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <iframe
            src={getChainlitIframeUrl()}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "6px",
            }}
            title="Chainlit Chat Interface"
          />
        </div>
      ) : (
        /* 原始RAG聊天功能 */
        <>
          {/* 消息列表 */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              marginBottom: "16px",
              border: "1px solid #f0f0f0",
              borderRadius: "6px",
              padding: "12px",
            }}
          >
            {messages.length === 0 ? (
              <Empty description="暂无对话" />
            ) : (
              <List
                dataSource={messages}
                renderItem={(messageItem) => (
                  <List.Item style={{ border: "none", padding: "8px 0" }}>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent:
                          messageItem.type === "user"
                            ? "flex-end"
                            : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "85%",
                          display: "flex",
                          alignItems: "flex-start",
                          flexDirection:
                            messageItem.type === "user" ? "row-reverse" : "row",
                        }}
                      >
                        <Avatar
                          icon={
                            messageItem.type === "user" ? (
                              <UserOutlined />
                            ) : (
                              <RobotOutlined />
                            )
                          }
                          style={{
                            backgroundColor:
                              messageItem.type === "user"
                                ? "#1890ff"
                                : "#52c41a",
                            margin:
                              messageItem.type === "user"
                                ? "0 0 0 8px"
                                : "0 8px 0 0",
                          }}
                        />
                        <div
                          style={{
                            backgroundColor:
                              messageItem.type === "user"
                                ? "#e6f7ff"
                                : "#f6ffed",
                            padding: "12px",
                            borderRadius: "8px",
                            border: `1px solid ${
                              messageItem.type === "user"
                                ? "#91d5ff"
                                : "#b7eb8f"
                            }`,
                            position: "relative",
                          }}
                        >
                          {messageItem.error && (
                            <Alert
                              message="錯誤"
                              description={messageItem.error}
                              type="error"
                              style={{ marginBottom: 8 }}
                            />
                          )}

                          <Paragraph
                            style={{ margin: 0, whiteSpace: "pre-wrap" }}
                          >
                            {messageItem.content}
                          </Paragraph>

                          {/* 處理步驟顯示 */}
                          {messageItem.processing_steps &&
                            showProcessingSteps && (
                              <Collapse
                                size="small"
                                style={{ marginTop: 12 }}
                                items={[
                                  {
                                    key: "1",
                                    label: (
                                      <Space>
                                        <ClockCircleOutlined />
                                        <Text>
                                          處理步驟 (
                                          {messageItem.processing_steps.length})
                                        </Text>
                                      </Space>
                                    ),
                                    children: (
                                      <div>
                                        {messageItem.processing_steps.map(
                                          (step, index) => (
                                            <div
                                              key={index}
                                              style={{ marginBottom: 8 }}
                                            >
                                              <Space>
                                                <Badge
                                                  count={index + 1}
                                                  size="small"
                                                  style={{
                                                    backgroundColor: "#52c41a",
                                                  }}
                                                />
                                                <Text strong>{step.step}</Text>
                                                {step.duration && (
                                                  <Tag color="blue">
                                                    {step.duration}ms
                                                  </Tag>
                                                )}
                                              </Space>
                                              <div
                                                style={{
                                                  marginLeft: 24,
                                                  marginTop: 4,
                                                }}
                                              >
                                                <Text type="secondary">
                                                  {step.description}
                                                </Text>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    ),
                                  },
                                ]}
                              />
                            )}

                          {/* 來源文檔顯示 */}
                          {messageItem.sources &&
                            messageItem.sources.length > 0 && (
                              <Collapse
                                size="small"
                                style={{ marginTop: 12 }}
                                items={[
                                  {
                                    key: "1",
                                    label: (
                                      <Space>
                                        <FileTextOutlined />
                                        <Text>
                                          參考來源 ({messageItem.sources.length}
                                          )
                                        </Text>
                                      </Space>
                                    ),
                                    children: (
                                      <div>
                                        {messageItem.sources.map(
                                          (source, index) => (
                                            <div
                                              key={index}
                                              style={{
                                                marginBottom: 12,
                                                padding: 8,
                                                backgroundColor: "#fafafa",
                                                borderRadius: 4,
                                              }}
                                            >
                                              <div style={{ marginBottom: 4 }}>
                                                <Text strong>
                                                  {source.title}
                                                </Text>
                                                {source.score && (
                                                  <Tag
                                                    color="green"
                                                    style={{ marginLeft: 8 }}
                                                  >
                                                    相關度:{" "}
                                                    {(
                                                      source.score * 100
                                                    ).toFixed(1)}
                                                    %
                                                  </Tag>
                                                )}
                                              </div>
                                              <Paragraph
                                                style={{
                                                  margin: 0,
                                                  fontSize: "12px",
                                                }}
                                                ellipsis={{
                                                  rows: 3,
                                                  expandable: true,
                                                }}
                                              >
                                                {source.content}
                                              </Paragraph>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    ),
                                  },
                                ]}
                              />
                            )}

                          <div
                            style={{
                              marginTop: 8,
                              fontSize: "12px",
                              color: "#999",
                            }}
                          >
                            {messageItem.timestamp.toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}

            {loading && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin size="large" />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">正在處理您的問題，請稍候...</Text>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* 快速問題建議 */}
          {messages.length <= 1 && (
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ marginBottom: 8, display: "block" }}>
                快速開始：
              </Text>
              <Space wrap>
                {quickQuestions.map((question, index) => (
                  <Button
                    key={index}
                    size="small"
                    onClick={() => setInputValue(question)}
                    style={{ marginBottom: 4 }}
                  >
                    {question}
                  </Button>
                ))}
              </Space>
            </div>
          )}

          {/* 輸入區域 */}
          <div
            style={{
              borderTop: "1px solid #f0f0f0",
              paddingTop: "16px",
            }}
          >
            <Space.Compact style={{ width: "100%" }}>
              <TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="請輸入您的問題..."
                autoSize={{ minRows: 1, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={loading}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => handleSendMessage()}
                loading={loading}
                disabled={!inputValue.trim()}
              >
                發送
              </Button>
            </Space.Compact>
            <div style={{ marginTop: 8, fontSize: "12px", color: "#999" }}>
              按 Enter 發送，Shift + Enter 換行
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default RAGChat;
